import React, { useState, useEffect, useMemo } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import DataTable from "examples/Tables/DataTable";
import { Select, MenuItem, InputLabel, FormControl, CircularProgress, Box, TextField } from "@mui/material"; // Import TextField
import LogoutButton from "layouts/logout";
import { useNavigate } from "react-router-dom";
import api from "core/interceptors/api";
import { disableBackNavigation } from "core/utils/storage";

function VendorCategory() {
  const [categories, setCategories] = useState([]); // Stores original fetched categories
  const [filteredCategories, setFilteredCategories] = useState([]); // Stores categories after vendor/search filter
  const email = localStorage.getItem("email");
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);

  // Pagination states
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  // Search query state
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    disableBackNavigation();
    fetchVendors();
    fetchCategories(); // Initial fetch for all categories (or admin's default)
  }, []);

  const [selectedVendor, setSelectedVendor] = useState("all");
  const [vendors, setVendors] = useState([]);

  // Memoize columns to prevent unnecessary re-renders of DataTable
  const columns = useMemo(() => [
    {
      Header: "S.No",
      accessor: "s_no",
      align: "center",
      // Dynamically calculate serial number based on current page and rowsPerPage
      Cell: ({ row }) => (page * rowsPerPage) + row.index + 1, 
    },
    {
      Header: "Category Name",
      accessor: "category_name",
      sortable: true,
    },
    {
      Header: "Parent Category",
      accessor: "parent_category_name",
      sortable: true,
    },
  ], [page, rowsPerPage]); // Depend on page and rowsPerPage so S.No updates correctly

  const handleVendorChange = (event) => {
    const vendorId = event.target.value;
    setSelectedVendor(vendorId);
    setPage(0); // Reset page on vendor change
    // Trigger category fetch based on new vendor
    if (vendorId === 'all') {
      fetchCategories(); // Fetch for admin's default email or general endpoint
    } else {
      const vendor = vendors.find(v => v.id === vendorId);
      fetchCategories(vendor ? vendor.email : 'all'); // Pass vendor's email
    }
  };

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
    setPage(0); // Reset page on search query change
  };

  // Effect hook to apply search filtering whenever categories or searchQuery changes
  useEffect(() => {
    const lowerCaseQuery = searchQuery.toLowerCase();
    const currentCategories = Array.isArray(categories) ? categories : [];

    const searchedData = currentCategories.filter(category => {
      // Search across relevant string fields
      return (
        category.category_name?.toLowerCase().includes(lowerCaseQuery) ||
        category.parent_category_name?.toLowerCase().includes(lowerCaseQuery)
        // Add more fields if needed for global search
      );
    });
    setFilteredCategories(searchedData);

    // Adjust page if current page is out of bounds after filtering
    if (page * rowsPerPage >= searchedData.length && searchedData.length > 0) {
      setPage(0);
    } else if (searchedData.length === 0 && page !== 0) {
      setPage(0);
    }
  }, [categories, searchQuery, page, rowsPerPage]);


  const fetchVendors = async () => {
    try {
      const res = await api.get(`/vendor_register/fetch_approved`);
      setVendors(res.data.vendors || []);
    } catch (error) {
      console.error("Error fetching vendors:", error);
    }
  };

  const fetchCategories = async (vendorEmailParam = email) => { // Default to current admin email
    setLoading(true);
    try {
      const res = await api.get(`/category/fetch`, {
        params: { email: vendorEmailParam },
      });
      setCategories(res.data || []); // Store original fetched data
    } catch (error) {
      console.error("Error fetching categories:", error);
      setCategories([]); // Clear categories on error
    } finally {
      setLoading(false);
    }
  };

  // Data for DataTable, based on filteredCategories (after vendor and search filters)
  const data = useMemo(() => Array.isArray(filteredCategories) ? filteredCategories.map((category) => ({
    // s_no will be handled by the Cell renderer in columns
    category_name: category.category_name,
    parent_category_name: category.parent_category_name,
  })) : [], [filteredCategories]); // Depend on filteredCategories

  // Pagination handlers
  const handleRowsPerPageChange = (event, newRowsPerPage) => {
    setRowsPerPage(newRowsPerPage);
    setPage(0); // Reset to first page when rows per page changes
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  // Apply pagination to the 'data' (which is already filtered)
  const paginatedTableData = data.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);


  return (
    <DashboardLayout>
      <MDBox display="flex" justifyContent="flex-end" alignItems="center">
        <LogoutButton
          onLogout={() => {
            navigate("/login");
          }}
        />
      </MDBox>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
              >
                <MDTypography variant="h6" color="white">
                  Vendor Categories
                </MDTypography>
              </MDBox>

              <MDBox p={3} display="flex" justifyContent="space-between" alignItems="flex-end">
                <FormControl sx={{ minWidth: 200, marginTop: 3, flexGrow: 0 }}>
                  <InputLabel id="vendor-select-label" >Select Vendor</InputLabel>
                  <Select
                    labelId="vendor-select-label"
                    value={selectedVendor}
                    onChange={handleVendorChange}
                    label="Select Vendor"
                    sx={{
                      height: 54, // Adjusted height for better alignment
                      paddingTop: "12px",
                      paddingBottom: "12px",
                    }}
                  >
                    <MenuItem value="all">All Vendors</MenuItem>
                    {(Array.isArray(vendors) && vendors.length > 0 ? vendors : []).map((vendor) => (
                      <MenuItem key={vendor.id} value={vendor.id}>
                        {vendor.owner_name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  label="Search categories..."
                  variant="outlined"
                  size="small"
                  value={searchQuery}
                  onChange={handleSearchChange}
                  sx={{ width: '300px', ml: 2 }} // Added margin-left for spacing
                />
              </MDBox>

              <MDBox p={3} display="flex" justifyContent="center" alignItems="center" minHeight="50svh">
                {loading ? (
                  <CircularProgress color="primary" />
                ) : (
                  <DataTable
                    table={{ columns: columns, rows: paginatedTableData }}
                    isSorted={true}
                    entriesPerPage={{ defaultValue: rowsPerPage, entries: [5, 10, 15, 20, 25] }} // Provided options for rows per page
                    showTotalEntries={true}
                    canSearch={false} // Global search is now handled externally
                    noEndBorder
                    pagination={{ // Added pagination prop
                      variant: "contained",
                      color: "dark",
                      page: page,
                      onPageChange: handlePageChange,
                      rowsPerPage: rowsPerPage,
                      onRowsPerPageChange: handleRowsPerPageChange,
                      count: filteredCategories.length, // Count should be based on filtered data
                    }}
                  />
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
    </DashboardLayout>
  );
}

export default VendorCategory;